// parts/PracticalMaterialLibrarySidebar.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalDropdownField, DropdownOption } from '../../../components/GlobalComponents/GlobalDropdownField';
import { PracticalMaterial } from '../PracticalMaterials';

export interface LibraryMaterial extends PracticalMaterial {
  course_title: string | null;
  module_title: string | null;
  module_order: number | null;
  chapter_title: string | null;
  chapter_order: number | null;
}

interface PracticalMaterialLibrarySidebarProps {
  visible: boolean;
  onHide: () => void;
  onReuse: (material: LibraryMaterial) => void;
  /** Course currently open on the page — used to flag "This course" materials */
  currentCourseId?: string;
}

interface StructureChapter { id: string; title: string; order: number; }
interface StructureModule { id: string; title: string; order: number; chapters: StructureChapter[]; }

type ScopeFilter = '' | 'course' | 'chapter';
type OwnerFilter = '' | 'institute' | 'branch';

const PAGE_LIMIT = 20;

// ── Chips ────────────────────────────────────────────────────────────────────

type ChipTone = 'course' | 'module' | 'chapter' | 'general' | 'current' | 'muted';

const CHIP_TONES: Record<ChipTone, { bg: string; color: string; border: string }> = {
  course:  { bg: '#e6f0f0', color: '#0f3d3e', border: '#b8d4d4' },
  module:  { bg: '#eef2ff', color: '#4338ca', border: '#c7d2fe' },
  chapter: { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' },
  general: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  current: { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0' },
  muted:   { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' },
};

const Chip: React.FC<{ tone: ChipTone; icon?: string; label: string; title?: string }> = ({ tone, icon, label, title }) => {
  const t = CHIP_TONES[tone];
  return (
    <span
      title={title ?? label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '5px', maxWidth: '100%',
        padding: '3px 9px', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600,
        background: t.bg, color: t.color, border: `1px solid ${t.border}`,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}
    >
      {icon && <i className={`pi ${icon}`} style={{ fontSize: '0.65rem', flexShrink: 0 }} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </span>
  );
};

const Sep = () => <i className="pi pi-angle-right" style={{ fontSize: '0.65rem', color: '#c0c0c0' }} />;

const OwnerBadge: React.FC<{ material: LibraryMaterial }> = ({ material }) => {
  const isInstitute = material.owner_level === 'institute';
  return (
    <span
      style={{
        fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', flexShrink: 0,
        background: isInstitute ? '#fef3c7' : '#e0f2fe',
        color: isInstitute ? '#92400e' : '#075985',
        textTransform: 'uppercase', letterSpacing: '0.3px',
      }}
    >
      {isInstitute ? 'Institute' : 'Branch'}
    </span>
  );
};

function Segmented<T extends string>({ value, options, onChange }: {
  value: T;
  options: { label: string; value: T }[];
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', background: '#eef3f3', borderRadius: '10px', padding: '3px', gap: '3px' }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value || 'all'}
            type="button"
            onClick={() => onChange(o.value)}
            style={{
              flex: 1, padding: '6px 8px', border: 'none', borderRadius: '8px', cursor: 'pointer',
              background: active ? '#0f3d3e' : 'transparent',
              color: active ? 'white' : '#475569',
              fontSize: '0.75rem', fontWeight: 600,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const FilterLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>
    {children}
  </div>
);

// ── Component ────────────────────────────────────────────────────────────────

const PracticalMaterialLibrarySidebar: React.FC<PracticalMaterialLibrarySidebarProps> = ({ visible, onHide, onReuse, currentCourseId }) => {
  const [items, setItems]     = useState<LibraryMaterial[]>([]);
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // Filters
  const [search, setSearch]       = useState('');
  const [query, setQuery]         = useState('');
  const [courseId, setCourseId]   = useState('');
  const [moduleId, setModuleId]   = useState('');
  const [chapterId, setChapterId] = useState('');
  const [scope, setScope]         = useState<ScopeFilter>('');
  const [owner, setOwner]         = useState<OwnerFilter>('');

  // Filter option sources
  const [courseOptions, setCourseOptions] = useState<DropdownOption[]>([{ label: 'All courses', value: '' }]);
  const [modules, setModules]             = useState<StructureModule[]>([]);

  const debounceRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestRef    = useRef(0);
  const coursesLoaded = useRef(false);

  // Course list (loaded once, first time the sidebar opens)
  useEffect(() => {
    if (!visible || coursesLoaded.current) return;
    coursesLoaded.current = true;
    apiClient
      .get('/v1/courses', { params: { limit: 100 } })
      .then((res) => {
        if (!res.data.success) return;
        setCourseOptions([
          { label: 'All courses', value: '' },
          ...res.data.data.map((c: any) => ({ label: c.title || c.name, value: c.course_id })),
        ]);
      })
      .catch(() => { coursesLoaded.current = false; });
  }, [visible]);

  // Modules/chapters for the selected course
  useEffect(() => {
    if (!courseId) { setModules([]); return; }
    let cancelled = false;
    apiClient
      .get(`/v1/practical-materials/course/${courseId}/structure`)
      .then((res) => { if (!cancelled) setModules(res.data.success ? res.data.data.modules || [] : []); })
      .catch(() => { if (!cancelled) setModules([]); });
    return () => { cancelled = true; };
  }, [courseId]);

  const fetchLibrary = useCallback(async (targetPage: number, append: boolean) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page: targetPage, limit: PAGE_LIMIT };
      if (query)    params.search      = query;
      if (courseId) params.course_id   = courseId;
      if (scope)    params.scope_type  = scope;
      if (owner)    params.owner_level = owner;
      if (scope !== 'course') {
        if (chapterId)     params.chapter_id = chapterId;
        else if (moduleId) params.module_id  = moduleId;
      }

      const response = await apiClient.get('/v1/practical-materials/library', { params });
      if (reqId !== requestRef.current) return; // a newer request superseded this one

      if (response.data.success) {
        setItems((prev) => (append ? [...prev, ...response.data.data] : response.data.data));
        setTotal(response.data.total ?? 0);
        setPage(targetPage);
      } else {
        setError('Failed to load materials library');
      }
    } catch (err: any) {
      if (reqId !== requestRef.current) return;
      setError(err.response?.data?.error || 'Failed to load materials library');
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  }, [query, courseId, moduleId, chapterId, scope, owner]);

  // Refetch page 1 whenever the sidebar opens or any filter changes
  useEffect(() => {
    if (visible) fetchLibrary(1, false);
  }, [visible, fetchLibrary]);

  // ── Filter handlers ───────────────────────────────────────────────
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQuery(value.trim()), 400);
  };

  const handleCourseChange = (value: string) => {
    setCourseId(value || '');
    setModuleId('');
    setChapterId('');
  };

  const handleModuleChange = (value: string) => {
    setModuleId(value || '');
    setChapterId('');
  };

  const handleScopeChange = (value: ScopeFilter) => {
    setScope(value);
    if (value === 'course') { setModuleId(''); setChapterId(''); }
  };

  const hasFilters = !!(query || courseId || scope || owner);

  const clearFilters = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearch('');
    setQuery('');
    setCourseId('');
    setModuleId('');
    setChapterId('');
    setScope('');
    setOwner('');
  };

  const selectedModule = modules.find((m) => m.id === moduleId) || null;

  const moduleOptions: DropdownOption[] = [
    { label: 'All modules', value: '' },
    ...modules.map((m) => ({ label: `${m.order}. ${m.title}`, value: m.id })),
  ];

  const chapterOptions: DropdownOption[] = selectedModule
    ? [
        { label: 'All chapters', value: '' },
        ...selectedModule.chapters.map((c) => ({ label: `${selectedModule.order}.${c.order} ${c.title}`, value: c.id })),
      ]
    : [];

  const showStructureFilters = !!courseId && scope !== 'course' && modules.length > 0;
  const hasMore = items.length < total;

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      showCloseIcon={false}
      style={{ width: '480px', maxWidth: '100vw' }}
      pt={{ content: { style: { padding: 0 } }, header: { style: { padding: 0, display: 'none' } } }}
    >
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
          padding: '28px 24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '160px', height: '160px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
        <button
          type="button"
          onClick={onHide}
          aria-label="Close"
          style={{
            position: 'absolute', top: '18px', right: '18px', zIndex: 2,
            width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '8px', color: 'white', cursor: 'pointer',
          }}
        >
          <i className="pi pi-times" style={{ fontSize: '0.9rem' }} />
        </button>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212, 241, 126, 0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            Practical Material Management
          </div>
          <h2 style={{ margin: '0 0 6px 0', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
            Reuse an Existing Material
          </h2>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
            Attach files and videos already uploaded elsewhere, without re-uploading
          </p>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '20px 24px', background: '#fafafa', minHeight: 'calc(100% - 118px)' }}>

        {/* Filters */}
        <div style={{ background: 'white', border: '1px solid #eef0f0', borderRadius: '14px', padding: '16px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <GlobalInputField
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by title..."
            icon="pi pi-search"
          />

          <GlobalDropdownField
            label="Course"
            value={courseId}
            onChange={(val) => handleCourseChange(val as string)}
            options={courseOptions}
            placeholder="All courses"
          />

          {showStructureFilters && (
            <div style={{ display: 'grid', gridTemplateColumns: selectedModule ? '1fr 1fr' : '1fr', gap: '10px' }}>
              <GlobalDropdownField
                label="Module"
                value={moduleId}
                onChange={(val) => handleModuleChange(val as string)}
                options={moduleOptions}
                placeholder="All modules"
              />
              {selectedModule && (
                <GlobalDropdownField
                  label="Chapter"
                  value={chapterId}
                  onChange={(val) => setChapterId((val as string) || '')}
                  options={chapterOptions}
                  placeholder="All chapters"
                />
              )}
            </div>
          )}

          <div>
            <FilterLabel>Scope</FilterLabel>
            <Segmented<ScopeFilter>
              value={scope}
              onChange={handleScopeChange}
              options={[
                { label: 'All', value: '' },
                { label: 'General', value: 'course' },
                { label: 'Chapter-wise', value: 'chapter' },
              ]}
            />
          </div>

          <div>
            <FilterLabel>Created at</FilterLabel>
            <Segmented<OwnerFilter>
              value={owner}
              onChange={setOwner}
              options={[
                { label: 'All', value: '' },
                { label: 'Institute', value: 'institute' },
              ]}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b', borderTop: '1px dashed #e5e7eb', paddingTop: '10px' }}>
            <span>
              {loading && items.length === 0 ? 'Searching…' : `Showing ${items.length} of ${total} material${total !== 1 ? 's' : ''}`}
            </span>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                style={{ background: 'none', border: 'none', color: '#0f3d3e', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}
              >
                <i className="pi pi-filter-slash" style={{ marginRight: '4px', fontSize: '0.7rem' }} />
                Clear filters
              </button>
            )}
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#b91c1c', fontSize: '0.82rem', marginBottom: '16px' }}>
            <i className="pi pi-exclamation-circle" style={{ marginRight: '6px' }} />
            {error}
          </div>
        )}

        {loading && items.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0' }}>
            <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" />
            <p style={{ marginTop: '14px', color: '#0f3d3e', fontSize: '0.85rem' }}>Loading library...</p>
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: '2px dashed #e0e0e0' }}>
            <i className="pi pi-inbox" style={{ fontSize: '2.5rem', color: '#ddd', marginBottom: '12px', display: 'block' }} />
            <h3 style={{ margin: '0 0 6px 0', color: '#666', fontSize: '0.95rem', fontWeight: 600 }}>No materials found</h3>
            <p style={{ margin: 0, color: '#999', fontSize: '0.82rem' }}>
              {hasFilters ? 'Try changing or clearing the filters' : 'No practical materials have been created yet'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {items.map((material) => {
              const isCurrentCourse = !!currentCourseId && material.course_id === currentCourseId;
              const isChapter = material.scope_type === 'chapter';
              const mOrder = material.module_order ?? '–';
              const cOrder = material.chapter_order ?? '–';

              return (
                <div
                  key={material.id}
                  style={{
                    padding: '14px',
                    border: `1px solid ${isCurrentCourse ? '#bbf7d0' : '#f0f0f0'}`,
                    borderRadius: '12px',
                    background: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  }}
                >
                  {/* Title row */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>
                      {material.title}
                    </span>
                    <OwnerBadge material={material} />
                  </div>

                  {/* Where it belongs: Course › Module › Chapter (or General) */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <Chip tone="course" icon="pi-book" label={material.course_title || 'Unknown course'} />
                    <Sep />
                    {isChapter ? (
                      <>
                        <Chip tone="module" icon="pi-sitemap" label={`${mOrder}. ${material.module_title || 'Module'}`} />
                        <Sep />
                        <Chip tone="chapter" icon="pi-bookmark" label={`${mOrder}.${cOrder} ${material.chapter_title || 'Chapter'}`} />
                      </>
                    ) : (
                      <Chip tone="general" icon="pi-globe" label="General · whole course" />
                    )}
                  </div>

                  {/* Meta chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                    {isCurrentCourse && <Chip tone="current" icon="pi-check-circle" label="This course" />}
                    <Chip tone="muted" icon="pi-sort-numeric-up" label={`Order ${material.order}`} />
                    {material.status !== 'active' && <Chip tone="muted" icon="pi-eye-slash" label={material.status} />}
                  </div>

                  {/* What will be reused */}
                  {(material.files?.length > 0 || material.videos?.length > 0) ? (
                    <div style={{ background: '#f8fafa', border: '1px solid #eef2f2', borderRadius: '8px', padding: '8px 10px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {material.files?.map((f) => (
                        <div key={f.key} style={{ fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <i className="pi pi-paperclip" style={{ marginRight: '6px', fontSize: '0.7rem' }} />{f.file_name}
                        </div>
                      ))}
                      {material.videos?.map((v) => (
                        <div key={v.video_id} style={{ fontSize: '0.75rem', color: v.status === 'ready' ? '#15803d' : '#b45309', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <i className={`pi ${v.status === 'ready' ? 'pi-play-circle' : 'pi-clock'}`} style={{ marginRight: '6px', fontSize: '0.7rem' }} />
                          {v.original_name}{v.status !== 'ready' ? ` (${v.status})` : ''}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.75rem', color: '#bbb', marginBottom: '12px' }}>No attachments — only title and content will be copied</div>
                  )}

                  <GlobalButton
                    label="Use This Material"
                    variant="secondary"
                    icon="pi pi-copy"
                    onClick={() => onReuse(material)}
                    fullWidth
                  />
                </div>
              );
            })}

            {hasMore && (
              <button
                type="button"
                onClick={() => fetchLibrary(page + 1, true)}
                disabled={loading}
                style={{
                  marginTop: '4px', padding: '10px', background: 'transparent',
                  border: '1px solid #e0e0e0', borderRadius: '10px', color: '#0f3d3e',
                  fontSize: '0.82rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? (
                  <><i className="pi pi-spin pi-spinner" style={{ marginRight: '6px' }} />Loading...</>
                ) : (
                  <><i className="pi pi-angle-down" style={{ marginRight: '6px' }} />Load More</>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default PracticalMaterialLibrarySidebar;